<script setup lang="ts">
    import type { ApiPermission } from '@/js/services/api/role/types';
    import { __, __untypedExpression } from '@/js/stores/expressions';
    import { RotateCcwIcon, CirclePlusIcon, CircleMinusIcon, InfoIcon } from '@lucide/vue';
    import EchoTooltip from '../EchoTooltip.vue';
    import EchoButton from '../EchoButton.vue';
    import EchoPopover from '../EchoPopover.vue';
    import IconButton from '../IconButton.vue';
    import {
        PermissionArea,
        DashboardModulePermissionFlag,
        VisiblePermissions,
    } from '@/js/consts/permissions';
    import { computed } from 'vue';

    const props = defineProps<{
        permissions: ApiPermission[];
        compareTo?: Record<string, number>;
        userName?: string;
        roleName?: string;
    }>();

    const emit = defineEmits<{
        'reset-area': [areaName: string];
    }>();

    const rolePermissions = defineModel<Record<string, number>>('rolePermissions', {
        required: true,
    });

    // Defined on the backend but not wired to any feature yet; hide until they do something.
    const UNIMPLEMENTED_DASHBOARD_FLAGS: ReadonlyArray<keyof typeof DashboardModulePermissionFlag> =
        ['ExportAsPdf', 'Schedule'];

    const UNIMPLEMENTED_FLAGS: Partial<Record<string, readonly string[]>> = {
        [PermissionArea.DashboardModule]: UNIMPLEMENTED_DASHBOARD_FLAGS,
    };

    const visiblePermissions = computed(() =>
        props.permissions.filter((perm) => VisiblePermissions.includes(perm.name))
    );

    const getFlags = (permission: ApiPermission) =>
        Object.entries(permission.valueDefinitions)
            .filter(([key]) => Number(key) !== 0)
            .filter(([, label]) => !UNIMPLEMENTED_FLAGS[permission.name]?.includes(label))
            .map(([key, label]) => ({
                flag: Number(key),
                label,
                translatedLabel: __untypedExpression('permission-' + label),
            }))
            .sort((a, b) => {
                if (a.flag === 1) return -1;
                if (b.flag === 1) return 1;
                return a.translatedLabel.localeCompare(b.translatedLabel);
            });

    const isFlagSet = (permission: ApiPermission, flag: number) =>
        ((rolePermissions.value[permission.name] ?? 0) & flag) === flag;

    const setFlag = (permission: ApiPermission, flag: number, checked: boolean) => {
        const selected = getFlags(permission).filter(({ flag: f }) =>
            f === flag ? checked : isFlagSet(permission, f)
        );
        rolePermissions.value = {
            ...rolePermissions.value,
            [permission.name]: selected.reduce((sum, { flag: f }) => sum | f, 0),
        };
    };

    type FlagDiff = 'added' | 'removed' | null;

    const flagDiffState = (permission: ApiPermission, flag: number): FlagDiff => {
        if (!props.compareTo) return null;
        const current = ((rolePermissions.value[permission.name] ?? 0) & flag) === flag;
        const compared = ((props.compareTo[permission.name] ?? 0) & flag) === flag;
        if (current === compared) return null;
        return current ? 'added' : 'removed';
    };

    const areaDiffersFromCompare = (permission: ApiPermission): boolean => {
        if (!props.compareTo) return false;
        return (
            (rolePermissions.value[permission.name] ?? 0) !==
            (props.compareTo[permission.name] ?? 0)
        );
    };

    const legendLabel = (kind: 'added' | 'removed') =>
        kind === 'added'
            ? __('user-permission-added-from-role', {
                  userName: props.userName ?? '',
                  roleName: props.roleName ?? '',
              })
            : __('user-permission-removed-from-role', {
                  userName: props.userName ?? '',
                  roleName: props.roleName ?? '',
              });
</script>

<template>
    <div class="flex flex-col gap-6">
        <div
            v-for="perm in visiblePermissions"
            :key="perm.id"
            class="flex flex-col gap-2 not-first:border-t border-t-gray-lightest not-first:pt-5"
        >
            <div class="flex items-center justify-between gap-2.5 flex-wrap mb-2">
                <h4 class="heading-sm">
                    {{ __untypedExpression('permission-area-' + perm.name) }}
                </h4>
                <div class="-my-1">
                    <EchoButton
                        v-if="compareTo && areaDiffersFromCompare(perm)"
                        variant="secondary"
                        size="small"
                        @click="emit('reset-area', perm.name)"
                    >
                        {{ __('reset-to-role-defaults') }}
                        <template #icon-left><RotateCcwIcon class="size-4" /></template>
                    </EchoButton>
                </div>
            </div>

            <div
                v-for="{ flag, label } in getFlags(perm)"
                :key="flag"
                class="inline-flex items-center py-0.5 text-body"
            >
                <label class="inline-flex items-center group cursor-pointer">
                    <input
                        type="checkbox"
                        class="Custom-checkbox"
                        :checked="isFlagSet(perm, flag)"
                        @change="(e) => setFlag(perm, flag, (e.target as HTMLInputElement).checked)"
                    />
                    {{ __untypedExpression('permission-' + label) }}
                </label>
                <EchoPopover>
                    <template #button="{ anchor, togglePopover, buttonAttrs }">
                        <IconButton
                            :ref="anchor"
                            class="ml-1"
                            size="size-4"
                            area-size="size-5"
                            :aria-label="__('show-info')"
                            v-bind="buttonAttrs"
                            @click="togglePopover"
                        >
                            <InfoIcon class="size-4 text-gray-dark" />
                        </IconButton>
                    </template>
                    <template #popover>
                        <div class="max-w-xs text-body">
                            {{
                                __untypedExpression(
                                    'permission-description-' + perm.name + '-' + label
                                )
                            }}
                        </div>
                    </template>
                </EchoPopover>
                <EchoTooltip
                    v-if="flagDiffState(perm, flag) === 'added'"
                    :label="legendLabel('added')"
                    class="ml-2 inline-flex"
                    tabindex="0"
                >
                    <CirclePlusIcon class="size-4 text-primary-carrot" />
                </EchoTooltip>
                <EchoTooltip
                    v-else-if="flagDiffState(perm, flag) === 'removed'"
                    :label="legendLabel('removed')"
                    class="ml-2 inline-flex"
                    tabindex="0"
                >
                    <CircleMinusIcon class="size-4 text-primary-carrot" />
                </EchoTooltip>
            </div>
        </div>
    </div>
</template>
